import axios from "axios";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import type {
  MissingBillingItem,
  ReplayRecoverableCommandConflictErrorType,
} from "@intelligems/ig-types";
import { IgButton, IgNudgeAlert, IgTypography } from "@intelligems/ig-theme";
import { useReplayRecoverableCommand } from "@src/api/billing-workflow/self-serve-recovery/replay-recoverable-command";
import { useResolveShopifyApprovalRedirect } from "@src/api/billing-workflow/shopify-approval/use-resolve-shopify-approval-redirect";
import { useGetLivePrices } from "@src/api/intelligems/get-live-prices";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { useGetOrganizationSummaryWithLoading } from "@src/api/organization-summaries/get-organization-summary-with-loading";
import { organizationsKeys } from "@src/api/queryKeys";
import { useCreateUsageBillingUrl } from "@src/api/shopify-app-subscriptions/create-usage-billing-url";
import { useGetBillingPortal } from "@src/api/stripe/get-billing-portal";
import { useIsAdmin } from "@src/api/user-auths/is-admin";
import { useDialogs } from "@src/hooks/dialog";
import { useDismissAlert } from "@src/hooks/useDismissAlert";
import { SettingsSectionId } from "@src/types/navigation";
import { toAnalyticsBillingCadence } from "@src/utils/billing-plan-intent";
import {
  mpBillingNudgeClick,
  mpBillingTrialReminderViewed,
} from "@src/utils/mixpanel";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";

import { SETTINGS_ROUTES } from "../../../routes/routes";

import { buildApprovalModules } from "./buildApprovalModules";
import {
  getShopifyApprovalRedirectId,
  shouldSuppressShopifyApprovalNudge,
} from "./shopify-approval-redirect";
import { trialReminderEvent } from "./trialReminderEvent";
import {
  BillingStatusContent,
  useBillingStatusContent,
} from "./useBillingStatusContent";
import { useGetBillingSubscriptionAccess } from "@src/api/billing-modules/subscription-access";

const INLINE_RETRY_ERROR_COPY: Record<MissingBillingItem, string> = {
  payment_method:
    "Looks like you still need a payment method. Add one in Stripe and try again.",
  address:
    "Looks like your billing address is still incomplete. Finish it in Stripe and try again.",
};

const CUSTOMER_DELETED_RETRY_COPY =
  "We couldn’t find your billing profile in Stripe. Please contact support to finish setting up your subscription.";

export const BillingNudgeAlert = () => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [showAfterDelay, setShowAfterDelay] = useState(false);
  const [hasClickedAddAddress, setHasClickedAddAddress] = useState(false);
  const [inlineRetryError, setInlineRetryError] = useState<string | null>(null);
  const displayCardContent = useBillingStatusContent();
  const getBillingPortal = useGetBillingPortal(false);
  const createUsageBillingUrl = useCreateUsageBillingUrl();
  const location = useLocation();
  const navigate = useNavigate();
  const dismissAlert = useDismissAlert();
  const isAdmin = useIsAdmin();
  const { openDialog } = useDialogs();
  const livePrices = useGetLivePrices();
  const queryClient = useQueryClient();
  const org = useGetOrg();
  const orgId = org.data?.org?.id;
  const orgSummary = useGetOrganizationSummaryWithLoading();
  const {
    data: subscriptionAccessData,
    isPending: isSubscriptionAccessPending,
  } = useGetBillingSubscriptionAccess();
  const isModularBilling = !!subscriptionAccessData?.org.isModular;
  const [isWorking, setIsWorking] = useState(false);

  const replayRecoverable = useReplayRecoverableCommand({
    onSuccess: () => {
      setInlineRetryError(null);
      if (orgId) {
        void queryClient.invalidateQueries({
          queryKey: organizationsKeys.orgSummary(orgId),
        });
      }
    },
    onError: (error) => {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        const conflict = error.response.data?.data as
          | ReplayRecoverableCommandConflictErrorType
          | undefined;
        if (conflict?.reason === "customer_deleted") {
          setInlineRetryError(CUSTOMER_DELETED_RETRY_COPY);
        } else {
          const missingBillingItem =
            conflict?.reason === "not_billing_ready"
              ? conflict.missingBillingItem
              : "address";
          setInlineRetryError(INLINE_RETRY_ERROR_COPY[missingBillingItem]);
        }
        // 409 is surfaced inline; suppress the global mutation error toast.
        (error as { suppressToast?: boolean }).suppressToast = true;
        return;
      }
      // 5xx and other errors fall through to the global toast handler.
      setInlineRetryError(null);
    },
  });

  const resolveShopifyApproval = useResolveShopifyApprovalRedirect({
    onError: (error) => {
      // 409 means the command already left waiting_on_shopify (merchant
      // approved out-of-band); suppress the toast and let the refetch clear
      // the nudge. 404 and other errors fall through to the global toast.
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        (error as { suppressToast?: boolean }).suppressToast = true;
      }
      if (orgId) {
        void queryClient.invalidateQueries({
          queryKey: organizationsKeys.orgSummary(orgId),
        });
      }
    },
  });

  const adjustBillingMutating = useIsMutating({
    mutationKey: ["adjustBilling"],
  });
  const patchUserMutating = useIsMutating({
    mutationKey: ["patchUser"],
  });
  const isOnboardingMutating =
    adjustBillingMutating > 0 || patchUserMutating > 0;

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowAfterDelay(true);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  const trialSub = orgSummary.data?.org?.stripeSubscriptionSummaries.find(
    (s) => s.status === "trialing"
  );

  useEffect(() => {
    if (
      !displayCardContent?.isTrialReminder ||
      isMinimized ||
      !showAfterDelay ||
      isOnboardingMutating ||
      !trialSub ||
      !orgSummary.data
    ) {
      return;
    }
    const event = trialReminderEvent(trialSub);
    if (!event) {
      return;
    }

    mpBillingTrialReminderViewed(event);
  }, [
    displayCardContent?.isTrialReminder,
    isMinimized,
    showAfterDelay,
    isOnboardingMutating,
    trialSub,
    orgSummary.data,
  ]);

  useEffect(() => {
    return () => {
      setIsWorking(false);
    };
  }, []);

  const handleButtonClick = (cardContent: BillingStatusContent) => {
    mpBillingNudgeClick(cardContent.cardMessage, cardContent.cta || "unknown", {
      action_type: cardContent.actionType,
    });

    if (cardContent.actionType === "stripe-billing") {
      getBillingPortal.mutate(
        {
          pathParams: undefined,
          queryParams: undefined,
          body: {
            stripeCustomerId:
              cardContent.delayedSubscription?.stripeCustomerId || undefined,
            stripeSubscriptionId:
              cardContent?.missingBilling?.stripeSubscriptionId,
          },
        },
        {
          // Only flip into Retry mode once the user is actually en route to
          // Stripe. Flipping earlier would strand them in retry-only UI when
          // the portal fetch itself fails.
          onSuccess: () => {
            if (cardContent.recoveryCommandId) {
              setHasClickedAddAddress(true);
              setInlineRetryError(null);
            }
          },
        }
      );
    } else if (
      cardContent.actionType === "shopify-billing" &&
      !isModularBilling
    ) {
      if (cardContent?.missingBilling?.id) {
        const product =
          cardContent.missingBilling.stripeProductLinks?.[0]?.stripeProduct;
        const subscriptionLinkId = cardContent.missingBilling.id;

        const nudgeCadence = toAnalyticsBillingCadence(
          cardContent.missingBilling.interval,
          cardContent.missingBilling.intervalCount
        );

        if (product) {
          const matchedPlan = livePrices.data?.prices.find(
            (p) => p.product.name === product.name
          );
          openDialog("approvePlanConfirmation", {
            planName: product.name,
            monthlyPrice: product.flatPrice,
            interval: cardContent.missingBilling.interval ?? "month",
            intervalCount: cardContent.missingBilling.intervalCount,
            features: matchedPlan?.features ?? [],
            subscriptionLinkId,
          });
        } else {
          createUsageBillingUrl.mutate({
            body: { subscriptionLinkId },
            queryParams: undefined,
            pathParams: undefined,
            analytics: {
              source: "legacy_nudge",
              billingCycle: nudgeCadence,
            },
          });
        }
      }
    } else if (
      cardContent.actionType === "shopify-billing" &&
      isModularBilling
    ) {
      if (cardContent?.missingBilling?.id) {
        const modules = buildApprovalModules(
          cardContent.missingBilling.stripeProductLinks ?? []
        );
        const selected = modules
          .map((module) => module.id)
          .filter((id): id is string => Boolean(id))
          .join(",");

        setIsWorking(true);

        void navigate(
          `${SETTINGS_ROUTES.Settings}?selected=${selected}#${SettingsSectionId.Billing}`
        );
      }
    } else if (cardContent.actionType === "shopify-approval") {
      const pendingApproval = cardContent.pendingShopifyApproval;
      const approvalId = getShopifyApprovalRedirectId(pendingApproval);
      if (approvalId) {
        resolveShopifyApproval.mutate({
          pathParams: { approvalId },
          queryParams: undefined,
          body: undefined,
          analytics: {
            source: "pending_approval_resume",
            ...(pendingApproval && "kind" in pendingApproval
              ? { approvalKind: pendingApproval.kind }
              : {}),
          },
        });
      }
    } else {
      void navigate(`${SETTINGS_ROUTES.Settings}#${SettingsSectionId.Billing}`);
      dismissAlert("free-trial-pending");
    }
  };

  if (
    !displayCardContent ||
    isMinimized ||
    !showAfterDelay ||
    isOnboardingMutating ||
    shouldSuppressShopifyApprovalNudge(
      displayCardContent.actionType,
      location.search
    )
  ) {
    return null;
  }

  const showRetry =
    hasClickedAddAddress && Boolean(displayCardContent.recoveryCommandId);

  const isCtaDisabled = isWorking || isSubscriptionAccessPending;

  return (
    <IgNudgeAlert
      title={displayCardContent.cardMessage}
      description={displayCardContent.secondaryText ?? undefined}
      action={
        displayCardContent.cta
          ? {
              disabled: isCtaDisabled,
              label: isCtaDisabled ? "Working..." : displayCardContent.cta,
              onClick: () => handleButtonClick(displayCardContent),
              variant: displayCardContent.buttonVariant,
              color: displayCardContent.buttonColor,
              loading:
                getBillingPortal.isPending ||
                createUsageBillingUrl.isPending ||
                resolveShopifyApproval.isPending,
              sx: displayCardContent.buttonBorder ?? undefined,
            }
          : undefined
      }
      onDismiss={isAdmin ? () => setIsMinimized(true) : undefined}
      dismissAriaLabel="Dismiss billing notice"
    >
      {showRetry && (
        <>
          {inlineRetryError && (
            <IgTypography variant="body2" color="error">
              {inlineRetryError}
            </IgTypography>
          )}
          <IgButton
            variant="contained"
            color="primary"
            fullWidth
            loading={replayRecoverable.isPending}
            onClick={() => {
              const commandId = displayCardContent.recoveryCommandId;
              if (!commandId) return;
              setInlineRetryError(null);
              replayRecoverable.mutate({
                pathParams: { commandId },
                queryParams: undefined,
                body: undefined,
              });
            }}
          >
            Retry now
          </IgButton>
        </>
      )}
    </IgNudgeAlert>
  );
};
